import {
  applyUpdate,
  type Doc as YDoc,
  encodeStateVectorFromUpdate,
} from 'yjs';

import { isBuildDebugEnabled } from './build-config';

/**
 * Result of a safe apply update operation.
 */
export type SafeApplyUpdateResult =
  | { success: true }
  | { success: false; error: Error; errorType: SafeApplyUpdateErrorType };

/**
 * Error types for corruption reporting.
 */
export type SafeApplyUpdateErrorType =
  | 'VALIDATION_FAILED'
  | 'APPLY_FAILED'
  | 'UNKNOWN';

/**
 * Options for safeApplyUpdate.
 */
export interface SafeApplyUpdateOptions {
  /**
   * If true, validate the update before applying using encodeStateVectorFromUpdate.
   * This catches some corrupt updates before they cause issues.
   * Default: false (for performance)
   */
  validate?: boolean;

  /**
   * Origin to pass to Y.applyUpdate for filtering in update handlers.
   */
  origin?: unknown;

  /**
   * Context for error logging (e.g., docId, operation name).
   */
  context?: string;

  /**
   * Callback when corruption is detected. Use this to report to monitoring.
   */
  onCorruption?: (info: {
    error: Error;
    errorType: SafeApplyUpdateErrorType;
    update: Uint8Array;
    context?: string;
  }) => void;
}

/**
 * Safely apply a Y.js update with error handling.
 *
 * SOLID: Single Responsibility - handles apply + error handling + optional validation
 * DRY: Central place for all applyUpdate error handling
 *
 * @param doc - The Y.Doc to apply the update to
 * @param update - The binary update to apply
 * @param options - Configuration options
 * @returns Result indicating success or failure with error details
 */
export function safeApplyUpdate(
  doc: YDoc,
  update: Uint8Array,
  options: SafeApplyUpdateOptions = {}
): SafeApplyUpdateResult {
  const { validate = false, origin, context, onCorruption } = options;

  // Optional validation before apply (catches some corrupt updates)
  if (validate) {
    try {
      encodeStateVectorFromUpdate(update);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Validation failed');

      console.error(
        `[safeApplyUpdate] Validation failed${context ? ` (${context})` : ''}: ${error.message}`
      );

      onCorruption?.({
        error,
        errorType: 'VALIDATION_FAILED',
        update,
        context,
      });

      return { success: false, error, errorType: 'VALIDATION_FAILED' };
    }
  }

  // Apply the update with error handling
  try {
    applyUpdate(doc, update, origin);
    return { success: true };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Apply update failed');

    console.error(
      `[safeApplyUpdate] Apply failed${context ? ` (${context})` : ''}: ${error.message}`
    );

    // Log first 20 bytes for debugging
    if (isBuildDebugEnabled()) {
      const preview = Array.from(update.slice(0, 20))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' ');
      console.debug(
        `[safeApplyUpdate] Update preview: [${preview}], size=${update.length}`
      );
    }

    onCorruption?.({
      error,
      errorType: 'APPLY_FAILED',
      update,
      context,
    });

    return { success: false, error, errorType: 'APPLY_FAILED' };
  }
}

/**
 * Safely apply update and throw on failure.
 * Use when you want to propagate errors but still get logging/reporting.
 */
export function safeApplyUpdateOrThrow(
  doc: YDoc,
  update: Uint8Array,
  options: SafeApplyUpdateOptions = {}
): void {
  const result = safeApplyUpdate(doc, update, options);
  if (!result.success) {
    throw result.error;
  }
}
